/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { useState } from 'react';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { VISIT_ACCENT_COLORS, useVisitStatusLabels } from '../constants/visit-colors';
import type { VisitStatus } from '../constants/visit-colors';
import type { KanbanVisit, VisitKanbanStatus } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';
import type { MobileKanbanLayout } from '../pages/ManageVisitsPage.helpers';
import { COLUMNS, ColumnCards, CountBadge } from './VisitsKanbanBoard.shared';
import {
  MobileTabsLayout,
  MobileStackLayout,
  MobileCarouselLayout,
} from './VisitsKanbanBoard.mobile';
import { Text } from '@/shared/ui';

interface VisitsKanbanBoardProps {
  visits: KanbanVisit[];
  selectedId: string | null;
  onSelectVisit: (id: string | null) => void;
  onStatusChange: (id: string, status: VisitKanbanStatus) => void;
  mobileLayout?: MobileKanbanLayout;
}

export const VisitsKanbanBoard: React.FC<VisitsKanbanBoardProps> = ({
  visits, selectedId, onSelectVisit, onStatusChange, mobileLayout = 'tabs',
}) => {
  const isMobile = useIsMobile();
  const statusLabels = useVisitStatusLabels();
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<VisitStatus | null>(null);

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverCol(null);
  };

  const handleDragOver = (e: React.DragEvent, col: VisitStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(col);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      setDragOverCol(null);
    }
  };

  const handleDrop = (e: React.DragEvent, col: VisitStatus) => {
    e.preventDefault();
    if (draggedId) {
      const dragged = visits.find((v) => v.id === draggedId);
      if (!dragged || dragged.status !== col) {
        onStatusChange(draggedId, col);
      }
    }
    setDraggedId(null);
    setDragOverCol(null);
  };

  const cardsByCol = (col: VisitStatus) => visits.filter((v) => v.status === col);

  if (isMobile) {
    if (mobileLayout === 'tabs') {
      return (
        <MobileTabsLayout
          visits={visits}
          selectedId={selectedId}
          onSelectVisit={onSelectVisit}
          onStatusChange={onStatusChange}
        />
      );
    }
    if (mobileLayout === 'stack') {
      return (
        <MobileStackLayout
          cardsByCol={cardsByCol}
          selectedId={selectedId}
          onSelectVisit={onSelectVisit}
        />
      );
    }
    return (
      <MobileCarouselLayout
        cardsByCol={cardsByCol}
        selectedId={selectedId}
        onSelectVisit={onSelectVisit}
      />
    );
  }

  // Desktop: four-column grid with drag-and-drop.
  return (
    <div
      data-testid="kanban-track"
      style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)', alignItems: 'start' }}
    >
      {COLUMNS.map((col) => {
        const accent = VISIT_ACCENT_COLORS[col];
        const cards = cardsByCol(col);
        const isDropTarget = dragOverCol === col;
        return (
          <div
            key={col}
            data-testid="kanban-column"
            onDragOver={(e) => handleDragOver(e, col)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, col)}
            style={{
              position: 'relative',
              background: 'var(--surface-primary)',
              borderRadius: 'var(--radius-lg)',
              border: isDropTarget ? `2px dashed ${accent}` : '1px solid var(--border-subtle)',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-sm)',
              transition: 'border 0.15s, box-shadow 0.15s',
            }}
          >
            <div style={{ padding: 'var(--space-3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text as="span" style={{ display: 'inline-block', paddingBottom: 'var(--space-1)', borderBottom: `2px solid ${accent}`, fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)' as unknown as number, color: 'var(--text-primary)' }}>
                {statusLabels[col]}
              </Text>
              <CountBadge accent={accent} count={cards.length} />
            </div>
            <div style={{ padding: 'var(--space-2)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', minHeight: 80 }}>
              <ColumnCards
                cards={cards}
                selectedId={selectedId}
                onSelectVisit={onSelectVisit}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
